import * as React from 'react'
import classnames from 'classnames'
import { connect } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router'

import Moment from 'react-moment'
import { Tooltip } from 'react-tippy'
import * as CurrencyFormat from 'react-currency-format'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Table, Button, Collapse, ButtonGroup, CustomInput } from 'reactstrap'

import { RootState } from '../store/rootReducer'
import { Claim } from '../lib/interfaces/Claim.interface'
import { Employee } from '../lib/interfaces/Employee.interface'

import VIN from './VIN.component'

import * as appSelectors from '../store/app/selectors'
import * as employeesActions from '../store/employees/actions'

import { formatName } from 'src/lib/helpers/format'

const moment = require('moment')

const get = require('lodash/get')
const map = require('lodash/map')
const compact = require('lodash/compact')
const indexOf = require('lodash/indexOf')
const includes = require('lodash/includes')

export type ClaimsListViewMode = 'card'|'table'

export type ClaimsListColumns = 'erac'|'vendor'|'location'|'vin'|'vehicle'|'driver'|'status'|'claim-type'|'loss-date'|'cost'

export interface ClaimsListViewModeTogglerProps {
  className?: string
  viewMode: ClaimsListViewMode
  onChange: (viewMode: ClaimsListViewMode) => void
}

export const DEFAULT_COLUMNS: ClaimsListColumns[] = [
  'erac',
  'vendor',
  'vin',
  'vehicle',
  'driver',
  'status',
  'claim-type',
  'cost'
]

export const COLUMN_TITLE = {
  erac: 'ERAC',
  vendor: 'Vendor',
  location: 'Location',
  vin: 'VIN',
  vehicle: 'Vehicle',
  driver: 'Driver',
  status: 'Status',
  'claim-type': 'Claim Type',
  'loss-date': 'Loss Date',
  cost: 'Cost'
}

export const ClaimsListViewModeToggler = (props: ClaimsListViewModeTogglerProps) => (
  <ButtonGroup className={props.className}>
    <Button
      outline
      size="sm"
      className="ggbutton"
      active={props.viewMode === 'table'}
      onClick={() => props.onChange('table')}
      color={props.viewMode === 'table' ? 'primary' : 'secondary'}
    >
      <FontAwesomeIcon icon={['fas', 'list']} />
    </Button>
    <Button
      outline
      size="sm"
      className="ggbutton"
      active={props.viewMode === 'card'}
      onClick={() => props.onChange('card')}
      color={props.viewMode === 'card' ? 'primary' : 'secondary'}
    >
      <FontAwesomeIcon icon={['fas', 'box']} />
    </Button>
  </ButtonGroup>
)

export interface ClaimsListProps extends RouteComponentProps {
  claims?: Claim[]
  className?: string
  selectable?: boolean
  emptyMessage?: string
  selectedIds?: number[]
  showDetailsOnClick?: boolean
  columns?: ClaimsListColumns[]
  viewMode?: ClaimsListViewMode
  renderFooter?: (claim: Claim) => any
  setCurrentEmployee: (employee: Employee) => void
  onRowClick?: (claim: Claim, index: number) => void
}

export interface ClaimsListState {
  expandedClaims: number[]
}

class ClaimsList extends React.PureComponent<ClaimsListProps, ClaimsListState> {
  state: ClaimsListState = {
    expandedClaims: []
  }

  handleToggleExpandClaim(claimId) {
    const { expandedClaims } = this.state

    let newExpandedClaims: number[] = []
    if (includes(expandedClaims, claimId)) {
      const claimIndex = indexOf(expandedClaims, claimId)
      newExpandedClaims = [ ...expandedClaims ]
      newExpandedClaims.splice(claimIndex, 1)
    }
    else {
      newExpandedClaims = [ ...expandedClaims, claimId ]
    }

    this.setState({ expandedClaims: newExpandedClaims })
  }

  renderEmployee(employee?: Employee, defaultValue?: string, underlined: boolean = true) {
    if (!employee) {
      return defaultValue
    }

    const { setCurrentEmployee } = this.props

    const employeeName = formatName(employee!)

    return (
      <a
        className="cursor-pointer"
        onClick={e => {
          e.stopPropagation()
          setCurrentEmployee(employee!)
        }}
      >
        {underlined
         ? <u>{employeeName}</u>
         : employeeName
        }
      </a>
    )
  }

  renderCurrencyValue(value) {
    return (
      <CurrencyFormat
        prefix="$"
        value={value}
        decimalScale={2}
        displayType="text"
        thousandSeparator={true}
        fixedDecimalScale={true}
      />
    )
  }

  renderVehicleInfo(claim) {
    const vehicleYear = claim.vehicleYear && `${moment(claim.vehicleYear).format('YYYY')} ` || ''
    return compact([vehicleYear, claim.vehicleMake, claim.vehicleModel]).join(' ')
  }

  renderStatus(claim) {
    let claimStatus = claim.claimStatus // apiv2
    
    if (!claimStatus) {
      claimStatus = claim.status // apiv1
    }

    if (claim.invoice) {
      return (
        <Tooltip followCursor className="d-flex" trigger="mouseenter" html={
          <div className="ggmouseover d-flex flex-column p-2">
            <span>
              Date paid:
              {' '}
              {claim.invoice.datePaid
                ? (
                  <strong>
                    <Moment date={claim.invoice.datePaid} format="MM.DD.YYYY" />
                  </strong>
                )
                : '-'
              }
            </span>
            <span>
              Paid by:
              {' '}
              <strong>{this.renderEmployee(claim.invoice.paidBy, '-', false)}</strong>
            </span>
            <span>
              Check number:
              {' '}
              {claim.invoice.checkNumber
                ? (
                  <strong>{claim.invoice.checkNumber}</strong>
                )
                : '-'
              }
            </span>
          </div>
        }>
          <a
            className="cursor-pointer"
            onClick={e => {
              e.stopPropagation()
              this.props.history.push(`/invoices/${claim.invoice.id}`)
            }}
          >
            <u>{claimStatus.name}</u>
          </a>
        </Tooltip>
      )
    }

    return get(claimStatus, 'name', '-')
  }

  renderTable() {
    const {
      claims,
      columns,
      className,
      onRowClick,
      selectable,
      selectedIds,
      showDetailsOnClick
    } = this.props

    const columnsToRender = columns || DEFAULT_COLUMNS

    return (
      <div className={classnames('claimslist', className)}>
        <Table hover className="table table-striped claims-table">
          <thead>
            <tr>
              {selectable && <th />}
              {map(columnsToRender, column => (
                <th key={column}>{COLUMN_TITLE[column]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {map(claims, (claim, index) => (
              <tr
                key={claim.id}
                className={classnames({ 'cursor-pointer': !!onRowClick })}
                onClick={() => {
                  if (onRowClick) {
                    onRowClick(claim, index)
                  }

                  if (showDetailsOnClick) {
                    this.setState({ expandedClaims: [claim.id] })
                  }
                }}
              >
                {selectable && (
                  <td>
                    <div className="w-100 h-100 d-flex align-items-center justify-content-center">
                      <CustomInput
                        inline
                        type="checkbox"
                        className="mr-0"
                        checked={includes(selectedIds, index)}
                      />
                    </div>
                  </td>
                )}
                {map(columnsToRender, column => {
                  let columnValue

                  switch(column) {
                    case 'erac':
                      columnValue = claim.enterpriseId || '-'
                      break

                    case 'vendor':
                      columnValue = get(claim, 'vendor.name', '-')
                      break

                    case 'location': {
                      const location = claim.accident && claim.accident.locationPosition && claim.accident.locationPosition.location
                      if (location) {
                        columnValue = location.name
                      } else { columnValue = '-' }
                      break
                    }

                    case 'vin':
                      columnValue = <VIN vin={claim.vehicleVin} />
                      break
                    
                    case 'vehicle':
                      columnValue = this.renderVehicleInfo(claim)
                      break

                    case 'driver':
                      columnValue = this.renderEmployee(claim.employee, '-')
                      break

                    case 'status':
                      columnValue = this.renderStatus(claim)
                      break

                    case 'claim-type':
                      columnValue = get(claim, 'claimType.name', '-')
                      break

                    case 'cost':
                      columnValue = this.renderCurrencyValue(claim.damage)
                      break

                    case 'loss-date': {
                      const accident = claim.accident
                      if (accident && accident.lossDate) {
                        columnValue = moment(accident.lossDate).format('MM/DD/YYYY hh:mm A')
                      } else { columnValue = '-' }
                      break
                    }

                    default:
                      break
                  }

                  return (
                    <td key={column}>{columnValue}</td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    )
  }

  render() {
    const { expandedClaims } = this.state
    const { claims, viewMode, className, renderFooter, emptyMessage } = this.props

    if (!claims || !claims.length) {
      return (
        <div className="ggcard d-flex align-items-center justify-content-center w-100 p-4 mt-2 mr-2">
          <p className="mb-0">
            <b>{emptyMessage || 'No claims to show.'}</b>
          </p>
        </div>
      )
    }

    if (viewMode === 'table') {
      return this.renderTable()
    }

    return (
      <div className={classnames('claimslist row no-gutters', className)}>
        {map(claims, claim => {
          const footer = renderFooter ? renderFooter(claim) : null

          return (
            <div key={claim.id} className="col-12 col-md-6 col-xl-4 pr-0 pr-md-3 mt-3">
              <div key={claim.id} className="ggcard claimscontainer">
                <div className="claimheader p-3" onClick={() => this.handleToggleExpandClaim(claim.id)}>
                  <table className="flex-grow-1">
                    <tbody>
                      <tr>
                        <td className="pr-3">Enterprise id:</td>
                        <td>{claim.enterpriseId || '-'}</td>
                      </tr>
                      <tr>
                        <td>VIN</td>
                        <td><VIN vin={claim.vehicleVin} /></td>
                      </tr>
                    </tbody>
                  </table>

                  <FontAwesomeIcon icon={['fas', 'angle-down']} className={classnames('ggcollapsible-icon', {
                    'ggcollapsible-icon__open': includes(expandedClaims, claim.id),
                  })} />
                </div>
                <Collapse isOpen={includes(expandedClaims, claim.id)}>
                  <div className="claim-collapsiblecontainer">
                    <div className="claimstatus flex-50 border-top border-right">
                      <b className="text-uppercase">Claim details</b>
                      <p>{`Status: ${get(claim, 'status.name', '-')}`}</p>
                      <p>{`Claim type: ${get(claim, 'claimType.name', '-')}`}</p>

                      <p>
                        Total Damage:
                        {' '}
                        <b>{this.renderCurrencyValue(claim.totalCost)}</b>
                      </p>
                    </div>
                    <div className="claimstatus flex-50 border-top">
                      <b className="text-uppercase">Invoice details</b>
                      <p>{`Vendor: ${get(claim, 'vendor.name', '-')}`}</p>                      
                      <p>{`Invoice received on: ${claim.invoiceReceived ? moment(claim.invoiceReceived).format('MM/DD/YYYY') : '-'}`}</p>
                    </div>
                    <div className="claimstatus flex-50 border-top border-right">
                      <b className="text-uppercase">Driver details</b>
                      <p>{`Driver ID: ${claim.employee && claim.employee.id || '-'}`}</p>
                      <p>
                        <span className="mr-1">Driver's name:</span>
                        {this.renderEmployee(claim.employee, '-')}
                      </p>
                    </div>
                    <div className="claimstatus flex-50 border-top">
                      <b className="text-uppercase">Vehicle details</b>
                      <p>Vehicle: {this.renderVehicleInfo(claim)}</p>
                      <p>
                        VIN:
                        <VIN vin={claim.vehicleVin} className="ml-2" />
                      </p>
                    </div>
                    <div className="claimstatus w-100 border-top">
                      <b className="text-uppercase">Claim description</b>
                      <p className="mb-0">{claim.description || '-'}</p>
                    </div>
                    {!!footer && (
                      <div className="claimstatus w-100 border-top">
                        {footer}
                      </div>
                    )}
                  </div>
                </Collapse>
              </div>
            </div>
          )
        })}
      </div>
    )
  }
}

const mapStateToProps = (state: RootState) => ({
  getLocationWithId: appSelectors.getLocationWithId(state)
})

const mapDispatchToProps = (dispatch) => ({
  setCurrentEmployee: (employee: Employee) => dispatch(employeesActions.setCurrentEmployee(employee))
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ClaimsList))



// WEBPACK FOOTER //
// ./src/common/ClaimsList.component.tsx